import { motion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { useTheme } from "@/lib/theme";
import CursorSpotlight from "@/components/site/CursorSpotlight";
import MagneticButton from "@/components/site/MagneticButton";

const HERO_IMG = "/tafra-hero-grid.webp";

const ease = [0.2, 0.7, 0.2, 1];

const Hero = ({ onOpenContact, onOpenAssessment }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Word + char stagger animation for the headline
  const line1Words = ["Powering"];
  const line2Words = ["Smarter"];

  const wordVariants = {
    hidden: { y: "110%", opacity: 0 },
    show: (i = 0) => ({
      y: "0%",
      opacity: 1,
      transition: { duration: 0.9, delay: 0.2 + i * 0.08, ease },
    }),
  };

  const buildings = "Buildings.";

  return (
    <section
      id="home"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center"
      style={{ backgroundColor: "var(--bg)" }}
      data-testid="hero-section"
    >
      {/* Image */}
      <motion.div
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1.02, opacity: 1 }}
        transition={{ duration: 1.6, ease }}
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: isDark ? "none" : "saturate(1.05)",
        }}
        aria-hidden
      />
      <div className={isDark ? "absolute inset-0 hero-mesh-dark" : "absolute inset-0 hero-mesh-light"} />
      <div className="absolute inset-0 energy-grid-bg opacity-40" />
      <div className="absolute inset-0 radial-fade opacity-90" />
      <div className="absolute inset-0 radial-crimson opacity-70" />
      <div className="grain-overlay" />

      <CursorSpotlight color="var(--accent-blue-glow)" />

      {/* Corner annotations */}
      <motion.div
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute top-24 right-8 md:right-12 z-10 hidden md:flex flex-col items-end gap-2 font-mono-spec text-[10px] tracking-[0.2em] uppercase"
        style={{ color: "var(--text-faint)" }}
      >
        <span>LAT —01.2864° S</span>
        <span>LON 36.8172° E</span>
        <span style={{ color: "var(--accent-blue)" }}>NBO · KE</span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-24 right-8 md:right-12 z-10 hidden lg:flex items-center gap-3 font-mono-spec text-[10px] tracking-[0.25em] uppercase"
        style={{ color: "var(--text-faint)" }}
      >
        <span className="w-12 h-px" style={{ background: "var(--accent-blue)" }} />
        <span>Design · Build · Commission</span>
      </motion.div>

      {/* Energy lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <span className="energy-line top-[22%] w-[40%]" style={{ animationDelay: "0s" }} />
        <span className="energy-line crimson top-[58%] w-[28%]" style={{ animationDelay: "2.5s" }} />
        <span className="energy-line top-[80%] w-[55%]" style={{ animationDelay: "5s" }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-16 pt-32 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <h1
              className="font-display text-[44px] sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight font-light"
              style={{ color: "var(--text)" }}
              data-testid="hero-headline"
            >
              {/* Line 1: Powering */}
              <span className="block overflow-hidden">
                {line1Words.map((w, i) => (
                  <motion.span
                    key={w}
                    custom={i}
                    initial="hidden"
                    animate="show"
                    variants={wordVariants}
                    className="inline-block will-change-transform"
                  >
                    {w}
                  </motion.span>
                ))}
              </span>

              {/* Line 2: Smarter Buildings. */}
              <span className="block overflow-hidden">
                {line2Words.map((w, i) => (
                  <motion.span
                    key={w}
                    custom={i + 1}
                    initial="hidden"
                    animate="show"
                    variants={wordVariants}
                    className="inline-block font-medium will-change-transform"
                  >
                    {w}
                  </motion.span>
                ))}{" "}
                <span className="relative inline-block">
                  <span className="relative inline-flex font-medium overflow-hidden">
                    {Array.from(buildings).map((c, i) => (
                      <motion.span
                        key={`${c}-${i}`}
                        initial={{ y: "110%", opacity: 0 }}
                        animate={{ y: "0%", opacity: 1 }}
                        transition={{
                          delay: 0.55 + i * 0.035,
                          duration: 0.8,
                          ease,
                        }}
                        style={{
                          backgroundImage: isDark
                            ? "linear-gradient(180deg,#3FB6FF,#7BD3FF)"
                            : "linear-gradient(180deg,#0B6FFF,#1E90FF)",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          color: "transparent",
                          display: "inline-block",
                          willChange: "transform",
                        }}
                      >
                        {c === " " ? "\u00A0" : c}
                      </motion.span>
                    ))}
                  </span>
                  <span
                    className="absolute -inset-2 blur-2xl -z-10 animate-pulse-glow"
                    aria-hidden
                    style={{ background: "var(--accent-blue-glow)" }}
                  />
                </span>
              </span>
            </h1>

            <motion.p
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.95, ease }}
              className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed"
              style={{ color: "var(--text-muted)" }}
              data-testid="hero-subheadline"
            >
              <span style={{ color: "var(--text)", fontWeight: 500 }}>
                TAFRA ENERGY LTD
              </span>{" "}
              is building a new digital home for premium Solar EPC and MEP
              Engineering solutions across Kenya.
            </motion.p>

            <motion.div
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.1, ease }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <MagneticButton
                onClick={onOpenContact}
                data-testid="hero-contact-btn"
                className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 font-semibold btn-sharp tracking-wide overflow-hidden"
                style={{
                  background: "var(--accent-blue)",
                  color: isDark ? "#000000" : "#FFFFFF",
                  boxShadow: `0 0 25px var(--accent-blue-glow)`,
                }}
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
                  }}
                />
                <span className="relative z-10">Contact Us</span>
                <ArrowRight className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </MagneticButton>

              <MagneticButton
                onClick={onOpenAssessment}
                data-testid="hero-assessment-btn"
                className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 btn-sharp tracking-wide overflow-hidden"
                style={{
                  background: "transparent",
                  color: "var(--text)",
                  border: "1px solid var(--accent-crimson)",
                  backdropFilter: "blur(4px)",
                }}
                strength={12}
              >
                <span
                  aria-hidden
                  className="absolute inset-0 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 -z-10"
                  style={{ background: "var(--accent-crimson)" }}
                />
                <Sparkles
                  className="w-4 h-4 transition-colors duration-300"
                  style={{ color: "var(--accent-crimson-hover)" }}
                />
                <span className="transition-colors duration-300 group-hover:text-white">
                  Request a Site Assessment
                </span>
              </MagneticButton>
            </motion.div>
          </div>

          {/* Right meta card */}
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25, ease }}
            className="lg:col-span-4"
          >
            <div
              className="relative p-6 btn-sharp backdrop-blur-xl"
              style={{
                background: "var(--surface-glass)",
                border: "1px solid var(--border)",
              }}
            >
              <span
                className="absolute -top-px right-6 h-px w-16"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
                }}
              />
              <span
                className="absolute -bottom-px left-6 h-px w-16"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, var(--accent-crimson), transparent)",
                }}
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin
                    className="w-4 h-4"
                    style={{ color: "var(--accent-blue)" }}
                    strokeWidth={1.6}
                  />
                  <span className="eyebrow !text-[0.65rem]">Based In</span>
                </div>
                <span
                  className="font-mono-spec text-[10px] tracking-[0.25em]"
                  style={{ color: "var(--text-faint)" }}
                >
                  T—01
                </span>
              </div>
              <p
                className="mt-3 font-display text-xl"
                style={{ color: "var(--text)" }}
              >
                Nairobi, Kenya
              </p>

              <div
                className="my-5 h-px diag-stripes"
                style={{ background: "var(--border)" }}
              />

              <div className="flex items-center gap-2">
                <Sparkles
                  className="w-4 h-4"
                  style={{ color: "var(--accent-crimson-hover)" }}
                  strokeWidth={1.6}
                />
                <span className="eyebrow eyebrow-crimson !text-[0.65rem]">Focus</span>
              </div>
              <p
                className="mt-3 text-sm leading-relaxed"
                style={{ color: "var(--text-muted)" }}
              >
                Solar EPC · MEP Engineering · Energy Optimization
              </p>

              <div
                className="mt-5 pt-4 flex items-center gap-2"
                style={{ borderTop: "1px dashed var(--border)" }}
                data-testid="hero-licensed-strip"
              >
                <span
                  aria-hidden
                  className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{
                    background: "#22c55e",
                    boxShadow: "0 0 8px rgba(34,197,94,0.7)",
                  }}
                />
                <span
                  className="font-mono-spec text-[10px] tracking-[0.25em] uppercase"
                  style={{ color: "var(--text)" }}
                >
                  EPRA · NCA Licensed
                </span>
              </div>
            </div>

            <div
              className="mt-4 flex items-center justify-between text-xs font-mono-spec tracking-widest"
              style={{ color: "var(--text-faint)" }}
            >
              <span>EST · ENGINEERING</span>
              <span style={{ color: "var(--accent-blue)" }}>v · 2026</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="eyebrow !text-[0.6rem] eyebrow-muted">Scroll to explore</span>
        <span
          className="block w-px h-10"
          style={{
            background: "linear-gradient(180deg, var(--accent-blue), transparent)",
          }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
